/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useContext } from 'react';
import { ThemeContext } from '../config/theme';
import SidebarModal from '../common/layouts/SidebarModal';
import UpsellSection from '../domain/upsell/UpsellSection';
import GlassesSection from '../domain/glasses/GlassesSection';
import { OrderContext, initOrderData } from '../common/providers/ContextProvider';
import OrderFooter from '../domain/order/OrderFooter';
import BackButton from '../common/components/BackButton/BackButton';
import { addToCart } from '../api';
import PrescriptionUpload from '../domain/prescription/PrescriptionUpload';
import { OrderStep } from '../utils/types';
import themedStyles from './App.css';

interface Options {
  variantId: number;
  defaultVariantId: number;
  productHandle: string;
  productPrice: number;
  productType: string;
}

interface EventListener {
  (evt: Event): void;
}

function App() {
  const [shouldMount, setShouldMount] = useState(false);
  const {
    currentStep,
    orderData,
    prescription,
    setParentProduct,
    revertOrderStep,
    getShopifyOrderData,
    resetContext,
  } = useContext(OrderContext);

  const changeBodyOverflow = (scroll: boolean) => {
    const scrollValue = scroll ? 'scroll' : 'hidden';
    document.getElementsByTagName('body')[0].style.overflow = scrollValue;
  };

  const mount = (event: CustomEvent<Options>) => {
    changeBodyOverflow(false);
    const variantId = event.detail.variantId
      ? event.detail.variantId
      : event.detail.defaultVariantId;
    setParentProduct({
      handle: event.detail.productHandle,
      variantId,
      price: event.detail.productPrice,
      type: event.detail.productType,
    });
    setShouldMount(true);
  };
  const unmount = (evt?: Event) => {
    evt && window.dispatchEvent(evt);
    changeBodyOverflow(true);
    setShouldMount(false);
    resetContext();
  };

  const placeOrder = async () => {
    const shopifyOrderData = await getShopifyOrderData();
    if (!shopifyOrderData) return;
    addToCart(shopifyOrderData);
    setTimeout(() => {
      unmount(new Event('react-app-cart-update'));
    }, 1000);
  };

  useEffect(() => {
    window.addEventListener('product-options:mount', mount as EventListener);
    window.addEventListener('product-options:unmount', unmount);
    return () => {
      window.removeEventListener('product-options:mount', mount as EventListener);
      window.removeEventListener('product-options:unmount', unmount);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const steps = [
    <GlassesSection key="glasses" />,
    <PrescriptionUpload key="prescription" />,
    <UpsellSection key="upsell" />,
  ];
  const hasSelectedGlass = orderData !== initOrderData;
  const showAdvance =
    (currentStep === OrderStep.Glasses && hasSelectedGlass) ||
    (currentStep === OrderStep.Prescription && prescription.title !== '') ||
    currentStep === OrderStep.Options;
  const showRevert = currentStep > 0;
  const styling = themedStyles(useContext(ThemeContext));
  if (!shouldMount) return null;
  return (
    <div css={styling.app}>
      <SidebarModal onClose={unmount}>
        {showRevert && <BackButton onClick={revertOrderStep} />}
        {steps[currentStep]}
        <OrderFooter
          display={hasSelectedGlass}
          enableAdvance={showAdvance}
          placeOrder={placeOrder}
        />
      </SidebarModal>
    </div>
  );
}
export default App;
